import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Clock, CheckCircle, XCircle, Users, Eye, MapPin } from "lucide-react";
import api from "../../api/client";
import { isPublishedJob } from "../../utils/jobSlug";
import { glassCardHover, glassPanel } from "../../utils/glassStyles";
import RecruiterPageShell from "../../components/recruiter/RecruiterPageShell";
import JobStatusBadge from "../../components/recruiter/JobStatusBadge";
import RecruiterEmptyState from "../../components/recruiter/RecruiterEmptyState";
import Loader from "../../components/ui/Loader";

export default function ApprovedJobsPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchApprovedJobs();
  }, []);

  const fetchApprovedJobs = async () => {
    try {
      const res = await api.get("/recruiter/jobs");
      setJobs((res.data || []).filter((job) => isPublishedJob(job)));
    } catch (err) {
      setError("Failed to load published jobs");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader label="Loading published jobs..." />;

  return (
    <RecruiterPageShell
      title="Published Jobs"
      subtitle="Live and expired job listings you've posted"
      onBack={() => navigate("/recruiter/dashboard")}
    >
      {error && <p className="text-destructive mb-4">{error}</p>}

      {jobs.length === 0 ? (
        <RecruiterEmptyState
          title="No published jobs yet"
          description="Once your jobs are approved, they'll appear here."
          actionLabel="Post a Job"
          onAction={() => navigate("/recruiter/post-job")}
        />
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <Card key={job.id} className={`rounded-2xl ${glassCardHover}`}>
              <CardContent className="p-5 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3
                        className="text-lg font-bold cursor-pointer hover:text-primary transition-colors"
                        onClick={() => navigate(`/recruiter/jobs/${job.id}`)}
                      >
                        {job.title}
                      </h3>
                      <JobStatusBadge status={job.status} job={job} />
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {job.location}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="outline" className="bg-yellow-500/10 border-yellow-300/40">
                    <Clock className="w-3 h-3 mr-1" />
                    {job.pendingCount || 0} Pending
                  </Badge>
                  <Badge variant="outline" className="bg-green-500/10 border-green-300/40">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {job.acceptedCount || 0} Accepted
                  </Badge>
                  <Badge variant="outline" className="bg-red-500/10 border-red-300/40">
                    <XCircle className="w-3 h-3 mr-1" />
                    {job.rejectedCount || 0} Rejected
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => navigate(`/recruiter/jobs/${job.id}/applicants`)}>
                    <Users className="w-4 h-4 mr-2" />
                    View Applicants
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/recruiter/jobs/${job.id}`)}>
                    <Eye className="w-4 h-4 mr-2" />
                    View Job
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </RecruiterPageShell>
  );
}
